import type { ReactNode } from "react";

import type { DesignSystemElementProps, DesignSystemRef } from "@/design-system/contracts/dom";
import { Spinner } from "@/design-system/Spinner";
import { Text } from "@/design-system/Text";

type DetailFieldContent =
  | {
      children: ReactNode;
      value?: never;
    }
  | {
      children?: never;
      value: string;
    };

export type DetailFieldProps = {
  label: string;
  contentWidth?: "narrow";
  isPending?: boolean;
} & DetailFieldContent &
  Omit<DesignSystemElementProps<"div">, "children"> &
  DesignSystemRef<HTMLDivElement>;

/** Labelled static or interactive detail content with a stable metadata rhythm. */
export function DetailField({
  children,
  contentWidth,
  isPending = false,
  label,
  ref,
  value,
  ...nativeProps
}: DetailFieldProps): ReactNode {
  const content =
    children ??
    (value ? (
      <Text variant="body-emphasis" overflow="normal">
        {value}
      </Text>
    ) : null);
  let renderedContent = content;

  if (isPending) {
    renderedContent = (
      <span className="my-space-xs ml-space-sm">
        <Spinner size="sm" />
      </span>
    );
  } else if (contentWidth === "narrow") {
    renderedContent = <div className="w-control-narrow">{content}</div>;
  }

  return (
    <div {...nativeProps} ref={ref} className="flex flex-col gap-xs pr-space-sm">
      <Text variant="caption" tone="secondary" overflow="normal">
        {label}
      </Text>
      <div className="flex min-h-control-md items-center">{renderedContent}</div>
    </div>
  );
}

/** A wrapping run of detail fields with the accepted metadata dividers and rhythm. */
export function DetailFieldGroup({ children }: { children: ReactNode }): ReactNode {
  return <div className="flex flex-wrap gap-sm divide-x divide-border-default">{children}</div>;
}

/** Compact inline content used inside a detail field. */
export function DetailFieldInline({ children }: { children: ReactNode }): ReactNode {
  return <span className="flex h-icon-prominent items-center gap-space-xs">{children}</span>;
}
